import { getNavigationPath, NavigationEntityType } from '@AppModels/hooks/useAINavigationResolver';
import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { createContext, ReactNode, useContext, useEffect, useId, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { StickToBottomContext } from 'use-stick-to-bottom';
import { useStreamingResponseMutation } from '../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useCeteraAIDisclaimer } from '../../../../models/hooks/useCeteraAIDisclaimer';
import { useEventLogging } from '../../../../models/Logging';
import { searchRequestToQueryStringParams } from '../../../../models/UiUtils';
import { useAdminAccountQuery } from '../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	getLastAIConversationChatMessage,
	invalidateAIConversationChatMessage,
	useAIConversationChatMessageQuery,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	prependAIConversationToFilterCache,
	useAIConversationFilterQuery,
} from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { FullScreenModalViewModel } from '../../../../viewmodels/AppViewModels';
import WorkingLionImageUrl from '../../../assets/aiAssistantThinking.png';
import { AIConversationMessage } from '../../../components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../components-v2/ai/ChatConversation';
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../../../components-v2/ai/PromptInput';
import { AI_THINKING_MESSAGES, ThinkingIndicator } from '../../../components-v2/ai/ThinkingIndicator';
import { Button } from '../../../components-v2/Button';
import { Avatar2 } from '../../../components/Avatar2';
import { CloseButton } from '../../../components/CloseButton';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ChevronRight } from '../../../components/svgs/icons/ChevronRight';
import { PlusIcon } from '../../../components/svgs/icons/PlusIcon';
import { AnimatedSparkleIcon } from '../../../components/ai/AnimatedSparkleIcon';
import { SparkleIcon } from '../../../components/svgs/icons/SparkleIcon';
import { SparklesIcon } from '../../../components/svgs/icons/SparklesIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { ROUTES } from '../../../constants/routes';
import { reactQueryClient } from '../../../ReactQueryProvider';
import { brandPrimary } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { s } from './styles';
import { syncAIStreamToRQCache } from './utils';

const GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT = `GoogleBusinessReviewReplyAssistant`;
const UNIVERSAL_ASSISTANT_NAME = `SiteWideAgent`;
const ADMIN_AGENT_NAME = `AdminAgent`;
const SUCCESS_MEETING_PREP_AGENT_NAME = `SuccessMeetingPrepAgent`;

// Shared between each assistant's child component and its error boundary so the
// boundary can write to the exact same react-query cache key the child reads from.
const conversationTypeFilter = (type: Api.AIConversationType): Api.AIConversationFilter => ({
	criteria: [{ property: Api.AIConversationFilterProperty.ConversationType, value: type }],
});
const SITEWIDE_CONVERSATION_FILTER = conversationTypeFilter(Api.AIConversationType.SiteWide);
const ADMIN_CONVERSATION_FILTER = conversationTypeFilter(Api.AIConversationType.Admin);
const SUCCESS_MEETING_PREP_CONVERSATION_FILTER = conversationTypeFilter(Api.AIConversationType.SuccessMeetingPrep);

// FE only type for handling error messages in chat
export interface AIChatMessageError extends Api.AIChatMessage {
	error: string;
	chatRequest: Api.AIChatRequest;
}

function CampaignComposerAssistantLayout({
	onClose,
	onNewChat,
	children,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	children: ReactNode;
}) {
	return (
		<div className={css(s.chatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon />
					<span className={css(s.chatWindowHeaderTitle)}>Ask Lev</span>
				</div>
				{onNewChat ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								className={css(s.plusIconButton)}
								style={onClose ? { marginRight: '24px' } : undefined}
								onClick={onNewChat}
							>
								<PlusIcon fillColor={brandPrimary} />
							</button>
						</TooltipTrigger>
						<TooltipContent>New Chat</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

const useAssistantConversation = (
	assistantName: string,
	isOpen: boolean,
	impersonationContext?: Api.IImpersonationContext
) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useAssistantConversation');
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`useAssistantConversation-${assistantName}`, error);
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiAssistantCreateConversationMutation.data),
		id: aiAssistantCreateConversationMutation.data?.id,
		impersonationContext,
	});
	useEffect(() => {
		if (isOpen && aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
			});
		}
	}, [aiAssistantCreateConversationMutation, isOpen, assistantName, impersonationContext]);
	return {
		conversationId: aiAssistantCreateConversationMutation.data?.id,
		aiConversationChatMessageQuery,
	};
};

export function AIChatAssistantMessage({
	children,
	isThinking = false,
}: {
	children: ReactNode;
	isThinking?: boolean;
}) {
	return (
		<div className={css(s.assitantMessage, isThinking && s.assitantMessageThinking)}>
			{isThinking ? (
				<AnimatedSparkleIcon styles={[s.assistantMessageSparkle]} />
			) : (
				<SparkleIcon styles={[s.assistantMessageSparkle]} />
			)}
			<div className={css(s.assistantMessageContent)}>{children}</div>
		</div>
	);
}

export function AIChatUserMessage({ children }: { children: ReactNode }) {
	return <div className={css(s.userMessage)}>{children}</div>;
}

interface Params {
	conversationId: string;
	chatRequest: Api.AIChatRequest;
	impersonationContext?: Api.IImpersonationContext;
}

export const useAIAssistantStreamingMutation = ({
	assistantName,
	onSuccess,
}: {
	assistantName: string;
	onError?: (error: Error) => void;
	onSuccess?: (params: Params) => void;
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const queryClient = useQueryClient();
	const logger = useEventLogging('useAIAssistantStreamingMutation');
	const assistantStreamingMutation = useStreamingResponseMutation({
		mutationFn: (params: Params) =>
			new Promise<Response>((resolve, reject) =>
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${params.conversationId}/stream`,
						impersonationContext: params.impersonationContext,
					}),
					'POST',
					params.chatRequest,
					resolve,
					reject
				)
			),
		onEvent: (event, params) => {
			if (event.event === 'Error') {
				logger.logEvent('AssistantChatStreamEvent-Error', {
					message: event.data,
				});
			}
			syncAIStreamToRQCache({
				event,
				queryClient,
				queryKey: getConversationChatMessageQueryKey({
					id: params.conversationId,
					impersonationContext: params.impersonationContext,
				}),
				chatRequest: params.chatRequest,
			});
		},
		onError: error => {
			console.error(error);
			logger.logApiError('AssistantChatStreamRequest-Error', error as Api.IOperationResultNoValue);
			errorMessages.pushApiError(error, 'Error while streaming AI assistant response');
		},
		onSuccess: params => {
			const lastMessage = getLastAIConversationChatMessage(params.conversationId, params.impersonationContext);
			if (!(lastMessage as AIChatMessageError)?.error) {
				invalidateAIConversationChatMessage(params.conversationId);
			}
			onSuccess?.(params);
		},
	});
	return assistantStreamingMutation;
};

/** @deprecated Use PromptInput instead */
export function AssistantChatInput({
	placeholder = 'Ask me anything...',
	onSubmit,
	streamingInProgress = false,
}: {
	placeholder?: string;
	onSubmit: (prompt: string) => void;
	streamingInProgress: boolean;
}) {
	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
	const handlePromptKeyPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
			ev.preventDefault();
			const prompt = textAreaRef.current.value.trim();
			if (prompt === '') {
				return;
			}
			if (streamingInProgress) {
				toast.error('Please wait for the current response to finish.');
				return;
			}
			onSubmit(prompt);
			textAreaRef.current.value = '';
		}
	};
	return (
		<textarea
			ref={textAreaRef}
			placeholder={placeholder}
			onKeyPress={handlePromptKeyPress}
			className={css(s.promptInput)}
		/>
	);
}

export function removeErrorMessageFromConversationCache(
	conversationId: string,
	impersonationContext?: Api.IImpersonationContext
) {
	const conversationQueryKey = getConversationChatMessageQueryKey({
		id: conversationId,
		impersonationContext,
	});
	const messages: Api.AIChatMessage[] = reactQueryClient.getQueryData(conversationQueryKey) ?? [];
	if ((messages.at(-1) as AIChatMessageError)?.error) {
		reactQueryClient.setQueryData(conversationQueryKey, messages.slice(0, -1));
	}
}

export function AIChatAssistantMessageError({ onTryAgain }: { onTryAgain: () => void }) {
	return (
		<AIChatAssistantMessage>
			<div className={css(s.errorMessageContainer)}>
				<div>Error</div>
				<p className={css(s.errorMessageDetails)}>Something went wrong with the assistant. Please try again.</p>
				<Button variant='destructive-secondary' size='sm' onClick={onTryAgain} styles={[s.errorMessageButton]}>
					Try Again
				</Button>
			</div>
		</AIChatAssistantMessage>
	);
}

export function GoogleReviewReplyAssistantChat({
	isOpen,
	onUseGoogleReviewReply,
	context,
	impersonationContext,
	onClose,
}: {
	isOpen: boolean;
	onUseGoogleReviewReply: (reply: string, sessionId?: string) => void;
	context?: {
		review: Api.IGoogleBusinessReview;
		companyName: string;
		reply: string;
	};
	impersonationContext?: Api.IImpersonationContext;
	onClose?: () => void;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const { conversationId, aiConversationChatMessageQuery } = useAssistantConversation(
		GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
		isOpen,
		impersonationContext
	);
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
	});
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				contexts: context
					? [
							{
								_type: 'GoogleReviewAIContext',
								reviewedCompanyName: context.companyName,
								review: context.review,
								reply: context.reply,
							} as Api.GoogleReviewAIContext,
						]
					: undefined,
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	const handleCraftReplyMessageClick = () => {
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				_type: 'AIActionRequest',
				action: {
					_type: 'GoogleReviewReplyAction',
					reviewId: context.review.id,
				} as Api.GoogleReviewReplyAction,
				contexts: [
					{
						_type: 'GoogleReviewAIContext',
						reviewedCompanyName: context.companyName,
						review: context.review,
						reply: context.reply,
					} as Api.GoogleReviewAIContext,
				],
			} as Api.AIActionRequest,
			impersonationContext,
		});
	};
	if (!isOpen) {
		return null;
	}
	if (!conversationId) {
		return (
			<CampaignComposerAssistantLayout onClose={onClose}>
				<LoadingSpinner />
			</CampaignComposerAssistantLayout>
		);
	}
	return (
		<CampaignComposerAssistantLayout onClose={onClose}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I can help you craft a response. Do you have any specific instructions for
						this message?
					</AIChatAssistantMessage>
					{context?.review &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming ? (
						<button className={css(bs.ctaButtonReverse, s.aiKitCta)} onClick={handleCraftReplyMessageClick}>
							<span>Craft Reply Message</span> <ChevronRight />
						</button>
					) : null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Add Google Review Reply Prompt' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

export function UniversalAssistantLayout({
	onClose,
	onNewChat,
	children,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	children: ReactNode;
}) {
	return (
		<div className={css(s.universalChatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon />
					<span className={css(s.chatWindowHeaderTitle)}>Ask Lev</span>
				</div>
				{onNewChat ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button className={css(s.plusIconButton)} onClick={onNewChat}>
								<PlusIcon fillColor={brandPrimary} />
							</button>
						</TooltipTrigger>
						<TooltipContent>New Chat</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

interface UniversalAssistantChatProps {
	onUseAIEmail: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact: (value: Api.AIContactReference) => void;
	onUseContactList: (contactList: Api.ContactListToolCall) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
	impersonationContext?: Api.IImpersonationContext;
}

export const AIAssistantStreamingMutationContext = createContext<ReturnType<
	typeof useAIAssistantStreamingMutation
> | null>(null);

export function AIAssistantStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: UNIVERSAL_ASSISTANT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

export function AdminAgentStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: ADMIN_AGENT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

export function SuccessMeetingPrepStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

export function UniversalAssistantChat({
	onUseAIEmail,
	onUseSocialContent,
	onUseGoogleReviewReply,
	onUseBlogContent,
	onUseContact,
	onUseContactList,
	onNavigate,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: UniversalAssistantChatProps) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('UniversalAssistantChat');
	const fullscreenModal = useFullscreenModal();
	const showCeteraDisclaimer = useCeteraAIDisclaimer();
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const conversationRef = useRef<StickToBottomContext>(null);
	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		// Call the callback first, then show the toast to avoid interfering with modal state
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		showCeteraDisclaimer();
	};
	const onUseBlogContentHandler = (title: string, content: Api.IRawRichTextContentState) => {
		onUseBlogContent(title, content);
		showCeteraDisclaimer();
	};
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: SITEWIDE_CONVERSATION_FILTER,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
	});
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createUniversalAssistantConversation({
				assistantName: UNIVERSAL_ASSISTANT_NAME,
				impersonationContext,
			});
		}
	}, [
		aiConversationFilterQuery.isSuccess,
		aiConversationFilterQuery.data,
		createUniversalAssistantConversation,
		impersonationContext,
	]);

	const conversationId = aiConversationFilterQuery.data?.[0]?.id;

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				impersonationContext,
			} as Api.AIMessageRequest,
		});
	};

	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContentHandler}
								onNavigate={onNavigate}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}

export function UniversalAssistantChatWithinComposer({
	onUseAIEmail,
	onUseSocialContent,
	onUseBlogContent,
	onClose,
	emailContext,
	socialContext,
	blogContext,
	isOpen,
	impersonationContext,
}: {
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseBlogContent?: (title: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onClose: () => void;
	emailContext?: { subject: string; body: Api.IRawRichTextContentState };
	socialContext?: { content: Api.IRawRichTextContentState };
	blogContext?: { title: string; content: Api.IRawRichTextContentState };
	isOpen: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const logger = useEventLogging('UniversalAssistantChat');
	const showCeteraDisclaimer = useCeteraAIDisclaimer();
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const conversationRef = useRef<StickToBottomContext>(null);
	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		// Call the callback first, then show the toast to avoid interfering with modal state
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		showCeteraDisclaimer();
	};
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const onUseSocialContentHandler = onUseSocialContent ?? defaultOnUseSocialContent(fullscreenModal);
	// Google revies are diabled because we don't have a path for it yet
	const onUseGoogleReviewReply: (reply: string) => void = undefined;
	const defaultBlogHandler = onUseBlogContent ?? defaultOnUseBlogContent(fullscreenModal);
	const onUseBlogContentHandler = (title: string, content: Api.IRawRichTextContentState) => {
		defaultBlogHandler(title, content);
		showCeteraDisclaimer();
	};
	const onUseContact = (contactInfo: Api.AIContactReference) => {
		history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
	};
	const onUseContactList = (contactList: Api.ContactListToolCall) => {
		const request = buildContactsSearchRequest(contactList);
		const searchParams = searchRequestToQueryStringParams(request, { sort: 'asc', sortBy: 'handle' });
		history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
	};

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		impersonationContext,
		filter: SITEWIDE_CONVERSATION_FILTER,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		impersonationContext,
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
	});
	const createUniversalAssistantConversationMutation = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const handleNewChatClick = () => {
		createUniversalAssistantConversationMutation.mutate({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	if (!aiAssistantStreamingMutation) {
		throw new Error('UniversalAssistantChatWithinComposer must be wrapped in AIAssistantStreamingMutationProvider');
	}

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			impersonationContext,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				contexts: (() => {
					const contexts = [
						...(emailContext
							? [
									{
										_type: 'EmailAIContext',
										subject: emailContext.subject,
										body: emailContext.body,
									} as Api.EmailAIContext,
								]
							: []),
						...(socialContext
							? [
									{
										_type: 'SocialMediaAIContext',
										content: socialContext.content,
									} as Api.SocialMediaAIContext,
								]
							: []),
						...(blogContext
							? [
									{
										_type: 'BlogAIContext',
										title: blogContext.title,
										content: blogContext.content,
									} as Api.BlogAIContext,
								]
							: []),
					];
					return contexts.length > 0 ? contexts : undefined;
				})(),
			} as Api.AIMessageRequest,
		});
	};
	if (!isOpen) {
		return null;
	}
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</CampaignComposerAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onUseSocialContent={onUseSocialContentHandler}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContentHandler}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

export function UniversalAssistantChatWithinSingleSendComposer({
	onUseAIEmail,
	onClose,
	emailContext,
	isOpen,
	impersonationContext,
	kitAIActionRequest,
}: {
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onClose: () => void;
	emailContext?: { subject: string; body: Api.IRawRichTextContentState };
	isOpen: boolean;
	impersonationContext?: Api.IImpersonationContext;
	kitAIActionRequest: Api.AIActionRequest;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const logger = useEventLogging('UniversalAssistantChatWithinSingleSendComposer');
	const showCeteraDisclaimer = useCeteraAIDisclaimer();
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const conversationRef = useRef<StickToBottomContext>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Lazy: no conversation is loaded or created until the user engages.
	const [conversationId, setConversationId] = useState<string | null>(null);

	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		showCeteraDisclaimer();
	};
	const onUseContact = (contactInfo: Api.AIContactReference) => {
		history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
	};
	const onUseContactList = (contactList: Api.ContactListToolCall) => {
		const request = buildContactsSearchRequest(contactList);
		const searchParams = searchRequestToQueryStringParams(request, { sort: 'asc', sortBy: 'handle' });
		history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
	};

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		impersonationContext,
		enabled: Boolean(conversationId),
		id: conversationId ?? undefined,
		staleTime: Infinity,
	});

	const createUniversalAssistantConversationMutation = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const ensureConversation = async (): Promise<string> => {
		if (conversationId) return conversationId;
		const result = await createUniversalAssistantConversationMutation.mutateAsync({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
			impersonationContext,
		});
		setConversationId(result.id);
		return result.id;
	};

	const handleNewChatClick = () => {
		setConversationId(null);
	};
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: UNIVERSAL_ASSISTANT_NAME,
	});

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!conversationId) return;
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = async ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		conversationRef.current?.scrollToBottom();
		const activeConversationId = await ensureConversation();
		removeErrorMessageFromConversationCache(activeConversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: activeConversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId: activeConversationId,
			impersonationContext,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				contexts: emailContext
					? [
							{
								_type: 'EmailAIContext',
								subject: emailContext.subject,
								body: emailContext.body,
							} as Api.EmailAIContext,
						]
					: undefined,
			} as Api.AIMessageRequest,
		});
	};
	const handleKITConversationStarterClick = async () => {
		const convId = await ensureConversation();
		aiAssistantStreamingMutation.mutate({
			conversationId: convId,
			chatRequest: kitAIActionRequest,
		});
	};
	if (!isOpen) {
		return null;
	}
	return (
		<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming ? (
						<Button variant='secondary' styles={[bs.ml4, bs.mr4]} onClick={handleKITConversationStarterClick}>
							Craft Keep-in-Touch Email <ChevronRight />
						</Button>
					) : null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId as string}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

/**
 * Maps string property names from AI SimpleFilter to ContactFilterCriteriaProperty enum values.
 * This is needed because the AI returns property names as strings, but the API expects enum values.
 */
const SIMPLE_FILTER_PROPERTY_MAP: Record<string, Api.ContactFilterCriteriaProperty> = {
	Tag: Api.ContactFilterCriteriaProperty.Tag,
	City: Api.ContactFilterCriteriaProperty.City,
	StateProvince: Api.ContactFilterCriteriaProperty.StateProvince,
	PostalCode: Api.ContactFilterCriteriaProperty.PostalCode,
	CompanyTag: Api.ContactFilterCriteriaProperty.CompanyTag,
	Name: Api.ContactFilterCriteriaProperty.Name,
	Company: Api.ContactFilterCriteriaProperty.Company,
	Source: Api.ContactFilterCriteriaProperty.Source,
	OwnedBy: Api.ContactFilterCriteriaProperty.OwnedBy,
	ConnectionType: Api.ContactFilterCriteriaProperty.ConnectionType,
	WithEmailAddress: Api.ContactFilterCriteriaProperty.WithEmailAddress,
	WithPhoneNumber: Api.ContactFilterCriteriaProperty.WithPhoneNumber,
	KeepInTouch: Api.ContactFilterCriteriaProperty.KeepInTouch,
	LastCommunicationDate: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
	UpcomingKeyDates: Api.ContactFilterCriteriaProperty.UpcomingKeyDates,
};

/** Maps SimpleFilter.op string values to CONTACT_FILTER_OPERATOR values. */
const SIMPLE_FILTER_OPERATOR_MAP: Record<string, Api.ContactFilterOperator> = {
	And: Api.CONTACT_FILTER_OPERATOR.And,
	Or: Api.CONTACT_FILTER_OPERATOR.Or,
	Not: Api.CONTACT_FILTER_OPERATOR.Not,
};

/**
 * Maps string property names (as sent by the AI backend) to ContactFilterCriteriaProperty enum values.
 * Needed because numeric-valued enum members serialize as strings from the AI ("Tag" → 9, "OwnedBy" → 3),
 * while string-valued members (City, StateProvince, PostalCode, CompanyTag) already match.
 */
const PROPERTY_NAME_TO_ENUM: Record<string, Api.ContactFilterCriteriaProperty> = {
	All: Api.ContactFilterCriteriaProperty.All,
	Connections: Api.ContactFilterCriteriaProperty.Connections,
	CreatedBy: Api.ContactFilterCriteriaProperty.CreatedBy,
	OwnedBy: Api.ContactFilterCriteriaProperty.OwnedBy,
	PrivateContacts: Api.ContactFilterCriteriaProperty.PrivateContacts,
	KeepInTouch: Api.ContactFilterCriteriaProperty.KeepInTouch,
	TagAlert: Api.ContactFilterCriteriaProperty.TagAlert,
	Name: Api.ContactFilterCriteriaProperty.Name,
	Company: Api.ContactFilterCriteriaProperty.Company,
	Tag: Api.ContactFilterCriteriaProperty.Tag,
	Email: Api.ContactFilterCriteriaProperty.Email,
	UpcomingKeyDates: Api.ContactFilterCriteriaProperty.UpcomingKeyDates,
	UpcomingKeyDatesNotScheduled: Api.ContactFilterCriteriaProperty.UpcomingKeyDatesNotScheduled,
	WithEmailAddress: Api.ContactFilterCriteriaProperty.WithEmailAddress,
	WithoutEmailAddresses: Api.ContactFilterCriteriaProperty.WithoutEmailAddresses,
	WithoutTags: Api.ContactFilterCriteriaProperty.WithoutTags,
	InProgressAutomations: Api.ContactFilterCriteriaProperty.InProgressAutomations,
	OnHoldAutomations: Api.ContactFilterCriteriaProperty.OnHoldAutomations,
	PreviousAutomations: Api.ContactFilterCriteriaProperty.PreviousAutomations,
	Policy: Api.ContactFilterCriteriaProperty.Policy,
	KeyDate: Api.ContactFilterCriteriaProperty.KeyDate,
	KeyDateKind: Api.ContactFilterCriteriaProperty.KeyDateKind,
	KeyDateYear: Api.ContactFilterCriteriaProperty.KeyDateYear,
	KeyDateSuppressList: Api.ContactFilterCriteriaProperty.KeyDateSuppressList,
	KeyDateSuppressDate: Api.ContactFilterCriteriaProperty.KeyDateSuppressDate,
	Archived: Api.ContactFilterCriteriaProperty.Archived,
	CapableOf: Api.ContactFilterCriteriaProperty.CapableOf,
	PhoneNumber: Api.ContactFilterCriteriaProperty.PhoneNumber,
	Source: Api.ContactFilterCriteriaProperty.Source,
	WithMailingAddress: Api.ContactFilterCriteriaProperty.WithMailingAddress,
	ConnectionTypeOrOwner: Api.ContactFilterCriteriaProperty.ConnectionTypeOrOwner,
	WithPhoneNumber: Api.ContactFilterCriteriaProperty.WithPhoneNumber,
	WithoutPhoneNumber: Api.ContactFilterCriteriaProperty.WithoutPhoneNumber,
	HasDuplicates: Api.ContactFilterCriteriaProperty.HasDuplicates,
	ConnectionType: Api.ContactFilterCriteriaProperty.ConnectionType,
	CreationDate: Api.ContactFilterCriteriaProperty.CreationDate,
	KeyFactOwnedBy: Api.ContactFilterCriteriaProperty.KeyFactOwnedBy,
	NamePrefix: Api.ContactFilterCriteriaProperty.NamePrefix,
	LastCommunicationDate: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
	City: Api.ContactFilterCriteriaProperty.City,
	StateProvince: Api.ContactFilterCriteriaProperty.StateProvince,
	PostalCode: Api.ContactFilterCriteriaProperty.PostalCode,
	CompanyTag: Api.ContactFilterCriteriaProperty.CompanyTag,
	// The backend C# enum assigns numeric values (39-42) to properties that the frontend
	// enum defines as string values. Map the backend's numeric values to the frontend strings.
	39: Api.ContactFilterCriteriaProperty.City,
	40: Api.ContactFilterCriteriaProperty.StateProvince,
	41: Api.ContactFilterCriteriaProperty.PostalCode,
	42: Api.ContactFilterCriteriaProperty.CompanyTag,
};

/**
 * Normalizes contact filter criteria from the AI backend by mapping string property names
 * to their corresponding ContactFilterCriteriaProperty enum values.
 * Recursively processes nested criteria arrays. Preserves op, value, and values as-is.
 */
// Valid ContactFilterCriteriaProperty values — used to check if a property is already a valid enum value
// (the audienceInfo path sends numeric values like 9 for Tag, while the contactList path sends strings)
const VALID_FILTER_PROPERTIES = new Set<unknown>(Object.values(Api.ContactFilterCriteriaProperty));

function normalizeContactFilterCriteria(criteria: Api.IContactFilterCriteria): Api.IContactFilterCriteria | null {
	const normalized: Api.IContactFilterCriteria = { ...criteria };

	if (criteria.property !== undefined) {
		const mapped = PROPERTY_NAME_TO_ENUM[criteria.property as unknown as string];
		if (mapped !== undefined) {
			normalized.property = mapped;
		} else if (VALID_FILTER_PROPERTIES.has(criteria.property)) {
			// Already a valid enum value (e.g., numeric 9 for Tag) — keep as-is
		} else {
			// Unrecognized property — the AI sometimes sends tag values as property names
			// (e.g., "Financial Services - Angel Investor/VC" instead of "Tag").
			console.warn(`[AI Filter] Stripping unrecognized filter property: ${String(criteria.property)}`);
			if (!criteria.criteria?.length) {
				// Leaf criterion — strip entirely so one bad criterion doesn't cause a 400
				return null;
			}
			// Compound criterion with children — clear the bad property but keep the children
			normalized.property = undefined;
		}
	}

	if (criteria.criteria?.length) {
		normalized.criteria = criteria.criteria
			.map(normalizeContactFilterCriteria)
			.filter((c): c is Api.IContactFilterCriteria => c !== null);
	}

	return normalized;
}

/**
 * Flattens contact filter criteria into a top-level array so that sortContactFilterCriteria
 * can classify each criterion into the correct bucket (searches/filters) for UI display.
 * Without flattening, nested/mixed criteria end up in the "compound" bucket and the filter
 * display at the top of the contacts page and email campaign recipient list won't show them.
 *
 * - And groups are recursively unwrapped (children promoted to top level, since top-level is implicitly And)
 * - Not filters with multiple children are expanded into individual Not entries
 * - Or groups are kept intact — PeopleSearchDescription handles Or groups directly
 *   (e.g., Or(StateProvince:"NY", StateProvince:"CA") renders as "NY, CA")
 * - Other compound criteria are kept intact
 * - Leaf criteria (with property, no nested criteria) are added directly
 */
function flattenContactFilterCriteria(criteria: Api.IContactFilterCriteria[]): Api.IContactFilterCriteria[] {
	const flat: Api.IContactFilterCriteria[] = [];
	for (const filter of criteria) {
		if (filter.op === Api.CONTACT_FILTER_OPERATOR.And && filter.criteria?.length) {
			// Date range compounds (e.g., LastCommunicationDate, CreationDate, KeyDate) use And
			// with Gte/Lte children — these must stay nested so the UI can parse the range.
			const hasRangeOperators = filter.criteria.some(
				c => c.op === Api.CONTACT_FILTER_OPERATOR.Gte || c.op === Api.CONTACT_FILTER_OPERATOR.Lte
			);
			if (hasRangeOperators) {
				flat.push(filter);
			} else {
				// And is associative — promote children to top level (recursively)
				flat.push(...flattenContactFilterCriteria(filter.criteria));
			}
		} else if (filter.op === Api.CONTACT_FILTER_OPERATOR.Not && filter.criteria?.length) {
			for (const criterion of filter.criteria) {
				flat.push({
					op: Api.CONTACT_FILTER_OPERATOR.Not,
					property: criterion.property,
					value: criterion.value,
				});
			}
		} else if (filter.op === Api.CONTACT_FILTER_OPERATOR.Or && filter.criteria?.length) {
			flat.push(filter);
		} else {
			flat.push(filter);
		}
	}
	return flat;
}

/**
 * Builds an IBulkContactsRequest from a ContactListToolCall for use in contacts page navigation.
 * Handles both the new contactFilterRequest format and the legacy SimpleFilter format.
 *
 * Extracts OwnedBy criteria from the filter and moves them to the ownershipFilter field,
 * since PeopleSearchDescription reads ownership from that separate field (not from filter.criteria).
 */
function parseContactListValue(contactList: Api.ContactListToolCall): Api.IBulkContactsRequest | null {
	// The API sends filter data inside the `value` JSON string as:
	// { "filters": "<IBulkContactsRequest JSON>", "totalcount": "12\n" }
	// Parse it out when the typed properties aren't populated.
	if (!contactList.value) {
		return null;
	}
	try {
		const parsed = JSON.parse(contactList.value);
		if (parsed?.filters && typeof parsed.filters === 'string') {
			return JSON.parse(parsed.filters) as Api.IBulkContactsRequest;
		}
	} catch {
		// parse error — fall through
	}
	return null;
}

function buildContactsSearchRequest(contactList: Api.ContactListToolCall): Api.IBulkContactsRequest {
	let criteria: Api.IContactFilterCriteria[];

	if (contactList.contactFilterRequest?.filter) {
		const normalized = normalizeContactFilterCriteria(contactList.contactFilterRequest.filter);
		criteria = normalized?.criteria?.length ? normalized.criteria : normalized ? [normalized] : [];
	} else {
		// Try parsing the filter from the raw `value` JSON string
		const parsedRequest = parseContactListValue(contactList);
		if (parsedRequest?.filter) {
			const normalized = normalizeContactFilterCriteria(parsedRequest.filter);
			criteria = normalized?.criteria?.length ? normalized.criteria : normalized ? [normalized] : [];
		} else {
			criteria = (contactList.filters ?? [])
				.map(convertSimpleFilter)
				.filter((c): c is Api.IContactFilterCriteria => c !== undefined);
		}
	}

	const flattened = flattenContactFilterCriteria(criteria);

	// Extract ownership criteria — PeopleSearchDescription reads from ownershipFilter, not filter.criteria
	const ownershipCriterion = flattened.find(c => c.property === Api.ContactFilterCriteriaProperty.OwnedBy);
	const remainingCriteria = ownershipCriterion ? flattened.filter(c => c !== ownershipCriterion) : flattened;

	const request: Api.IBulkContactsRequest = {
		filter: { criteria: remainingCriteria },
		ownershipFilter: ownershipCriterion
			? { criteria: ownershipCriterion }
			: { criteria: { property: Api.ContactFilterCriteriaProperty.All } },
	};

	return request;
}

/**
 * Converts a SimpleFilterCriteria to IContactFilterCriteria if the property is supported.
 * Returns undefined if the property is not recognized.
 *
 * Handles special cases:
 * - LastCommunicationDateAfter → LastCommunicationDate with Gte op
 * - LastCommunicationDate with value "never" → LastCommunicationDate with no value
 * - LastCommunicationDate with a date → LastCommunicationDate with Lte op
 */
function convertSimpleFilterCriteria(criteria: {
	property: string;
	value: string;
}): Api.IContactFilterCriteria | undefined {
	// Special case: LastCommunicationDateAfter → LastCommunicationDate with Gte
	if (criteria.property === 'LastCommunicationDateAfter') {
		return {
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			value: criteria.value,
			op: Api.CONTACT_FILTER_OPERATOR.Gte,
		};
	}

	// Special case: LastCommunicationDate with "never" → no value (contacts page "Never" preset)
	if (criteria.property === 'LastCommunicationDate' && criteria.value?.toLowerCase() === 'never') {
		return {
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
		};
	}

	// Special case: LastCommunicationDate with a date → Lte op
	if (criteria.property === 'LastCommunicationDate') {
		return {
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			value: criteria.value,
			op: Api.CONTACT_FILTER_OPERATOR.Lte,
		};
	}

	const enumProperty = SIMPLE_FILTER_PROPERTY_MAP[criteria.property];
	if (enumProperty === undefined) {
		return undefined;
	}
	return {
		property: enumProperty,
		value: criteria.value,
	};
}

/**
 * Converts a full SimpleFilter (with op + criteria[]) into an IContactFilterCriteria.
 * - Maps each criterion via convertSimpleFilterCriteria
 * - Maps SimpleFilter.op to CONTACT_FILTER_OPERATOR values
 * - Single criterion + Not → wraps in { op: Not, criteria: [single] }
 * - Single criterion + And/Or → returns criterion directly
 * - Multiple criteria → wraps in { op, criteria: [...] }
 */
function convertSimpleFilter(filter: Api.SimpleFilter): Api.IContactFilterCriteria | undefined {
	const converted = filter.criteria
		.map(convertSimpleFilterCriteria)
		.filter((c): c is Api.IContactFilterCriteria => c !== undefined);

	if (converted.length === 0) {
		return undefined;
	}

	// Support both `op` (current) and `operator` (legacy, from old stored AI chat messages)
	const rawOp = filter.op ?? filter.operator;
	const mappedOp = rawOp ? SIMPLE_FILTER_OPERATOR_MAP[rawOp] : undefined;

	if (converted.length === 1) {
		// Not requires wrapping even for a single criterion
		if (mappedOp === Api.CONTACT_FILTER_OPERATOR.Not) {
			return {
				op: Api.CONTACT_FILTER_OPERATOR.Not,
				criteria: [converted[0]],
			};
		}
		return converted[0];
	}

	return {
		op: mappedOp ?? Api.CONTACT_FILTER_OPERATOR.And,
		criteria: converted,
	};
}

/**
 * Converts ContactAudienceInfo from the AI to contact selection data for campaign creation.
 * Supports both tagFilters (for dynamic audience) and contactIds (for explicit contacts).
 * Uses convertSimpleFilter to properly handle each filter's op (And/Or/Not).
 *
 * IMPORTANT: The contactFilterRequest must produce a flat criteria array (not deeply nested)
 * because TagEmailRecipientsList checks hasUserSelectedContactFilterSearchCriteria, which
 * classifies criteria via sortContactFilterCriteria. Deeply nested compound criteria
 * (mixing SearchCriteriaProperties and SearchCriteriaFilterProperties) end up in the
 * "compound" bucket, which isn't recognized as user-selected, causing the UI to show
 * "pick audience" instead of the loaded recipients.
 */
function convertAudienceInfoToContactSelection(audienceInfo: Api.ContactAudienceInfo): {
	bulkFilterRequest?: Api.IEmailMessageContactsFilterRequest;
	contactIds?: string[];
} | null {
	// Prefer native contactFilterRequest from AI (new format)
	if (audienceInfo.contactFilterRequest?.filter) {
		const normalized = normalizeContactFilterCriteria(audienceInfo.contactFilterRequest.filter);
		const topCriteria = normalized?.criteria?.length ? normalized.criteria : normalized ? [normalized] : [];
		const flattened = flattenContactFilterCriteria(topCriteria);

		// Extract ownership criteria — TagEmailRecipientsList reads from ownershipFilter, not filter.criteria
		const ownershipCriterion = flattened.find(c => c.property === Api.ContactFilterCriteriaProperty.OwnedBy);
		const remainingCriteria = ownershipCriterion ? flattened.filter(c => c !== ownershipCriterion) : flattened;

		return {
			bulkFilterRequest: {
				contactFilterRequest: {
					criteria: remainingCriteria,
				},
				...(ownershipCriterion ? { ownershipFilter: { criteria: ownershipCriterion } } : {}),
			},
		};
	}

	// Handle tagFilters - for dynamic audience selection (also includes location filters) [fallback for old format]
	if (audienceInfo.tagFilters?.length) {
		const convertedFilters = audienceInfo.tagFilters
			.map(convertSimpleFilter)
			.filter((c): c is Api.IContactFilterCriteria => c !== undefined);

		if (convertedFilters.length === 0) {
			return null;
		}

		const flattened = flattenContactFilterCriteria(convertedFilters);

		// Extract ownership criteria — TagEmailRecipientsList reads from ownershipFilter, not filter.criteria
		const ownershipCriterion = flattened.find(c => c.property === Api.ContactFilterCriteriaProperty.OwnedBy);
		const remainingCriteria = ownershipCriterion ? flattened.filter(c => c !== ownershipCriterion) : flattened;

		return {
			bulkFilterRequest: {
				contactFilterRequest: {
					criteria: remainingCriteria,
				},
				...(ownershipCriterion ? { ownershipFilter: { criteria: ownershipCriterion } } : {}),
			},
		};
	}

	// Handle contactIds - for explicit contact selection
	if (audienceInfo.contactIds?.length) {
		return {
			contactIds: audienceInfo.contactIds,
		};
	}

	return null;
}

const defaultOnUseAIEmail =
	(fullscreenModal: FullScreenModalViewModel) =>
	(
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		const contactSelection = audienceInfo ? convertAudienceInfoToContactSelection(audienceInfo) : null;

		fullscreenModal.history.push({
			pathname: '/email/campaigns/create/from-scratch',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						...(contactSelection || {}),
						subject,
						body: content,
						aiReference,
					},
				},
			},
		});
	};
const defaultOnUseSocialContent =
	(fullscreenModal: FullScreenModalViewModel) =>
	(content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => {
		fullscreenModal.history.push({
			pathname: `/social-media/post/create`,
			state: {
				initialContent: content,
				aiReference,
			},
		});
	};
const defaultOnUseBlogContent =
	(fullscreenModal: FullScreenModalViewModel) =>
	(subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => {
		fullscreenModal.history.push({
			pathname: `/blog/post/create`,
			state: {
				initialContent: content,
				initialSubject: subject,
				aiReference,
			},
		});
	};

const UniversalAssistantChatErrorBoundary = ({ children }: { children: ReactNode }) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation, isPending } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});
	return (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => {
				const handleNewChatClick = () => {
					createUniversalAssistantConversation(
						{
							assistantName: UNIVERSAL_ASSISTANT_NAME,
						},
						{
							onSuccess: newConversation => {
								// Seed the cache with the new conversation so the remounted
								// children read fresh data at data[0] instead of the broken
								// conversation that tripped the boundary.
								prependAIConversationToFilterCache({ filter: SITEWIDE_CONVERSATION_FILTER }, newConversation);
								resetErrorBoundary();
							},
						}
					);
				};
				return (
					<UniversalAssistantLayout onNewChat={handleNewChatClick}>
						<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
							<WarningIcon />
							<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
							<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick} disabled={isPending}>
								Start new chat
							</Button>
						</div>
					</UniversalAssistantLayout>
				);
			}}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function DefaultUniversalAssistantChat(props: Partial<UniversalAssistantChatProps>) {
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	return (
		<UniversalAssistantChatErrorBoundary>
			<UniversalAssistantChat
				onUseAIEmail={defaultOnUseAIEmail(fullscreenModal)}
				onUseSocialContent={defaultOnUseSocialContent(fullscreenModal)}
				/* onUseGoogleReviewReply={_reply => {
			}} */
				onUseBlogContent={defaultOnUseBlogContent(fullscreenModal)}
				onUseContact={contactInfo => {
					history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
				}}
				onUseContactList={contactList => {
					const request = buildContactsSearchRequest(contactList);
					const searchParams = searchRequestToQueryStringParams(request, { sort: 'asc', sortBy: 'handle' });
					history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
				}}
				onNavigate={(entityType, entityId, parentEntityType, parentEntityId) => {
					const path = getNavigationPath(
						entityType as NavigationEntityType,
						entityId,
						parentEntityType as NavigationEntityType,
						parentEntityId
					);
					history.push(path);
				}}
				{...props}
			/>
		</UniversalAssistantChatErrorBoundary>
	);
}

interface AdminAppAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact?: (value: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		company?: string;
	}) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

const AdminAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation, isPending } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	return (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => {
				const handleNewChatClick = () => {
					createUniversalAssistantConversation(
						{
							assistantName: ADMIN_AGENT_NAME,
							impersonationContext,
						},
						{
							onSuccess: newConversation => {
								prependAIConversationToFilterCache(
									{ filter: ADMIN_CONVERSATION_FILTER, impersonationContext },
									newConversation
								);
								resetErrorBoundary();
							},
						}
					);
				};
				return (
					<UniversalAssistantLayout onNewChat={handleNewChatClick}>
						<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
							<WarningIcon />
							<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
							<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick} disabled={isPending}>
								Start new chat
							</Button>
						</div>
					</UniversalAssistantLayout>
				);
			}}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function AdminAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<AdminAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<AdminAssistantChat {...props} />
		</AdminAssistantChatErrorBoundary>
	);
}

export function AdminAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const id = useId();
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('AdminAppAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: ADMIN_CONVERSATION_FILTER,
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: ADMIN_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createUniversalAssistantConversation({
				assistantName: ADMIN_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createUniversalAssistantConversation]);

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};

	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}

	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, how can I help you with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}?
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmail}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContent}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}

const SuccessMeetingPrepAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('SuccessMeetingPrepAssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createSuccessMeetingPrepConversation, isPending } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	return (
		<ErrorBoundary
			fallbackRender={({ resetErrorBoundary }) => {
				const handleNewChatClick = () => {
					createSuccessMeetingPrepConversation(
						{
							assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
							impersonationContext,
						},
						{
							onSuccess: newConversation => {
								prependAIConversationToFilterCache(
									{ filter: SUCCESS_MEETING_PREP_CONVERSATION_FILTER, impersonationContext },
									newConversation
								);
								resetErrorBoundary();
							},
						}
					);
				};
				return (
					<UniversalAssistantLayout onNewChat={handleNewChatClick}>
						<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
							<WarningIcon />
							<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
							<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick} disabled={isPending}>
								Start new chat
							</Button>
						</div>
					</UniversalAssistantLayout>
				);
			}}
			onError={(error: Error) => {
				logger.logApiError('SuccessMeetingPrepAssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function SuccessMeetingPrepAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<SuccessMeetingPrepStreamingMutationProvider>
			<SuccessMeetingPrepAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
				<SuccessMeetingPrepAssistantChat {...props} />
			</SuccessMeetingPrepAssistantChatErrorBoundary>
		</SuccessMeetingPrepStreamingMutationProvider>
	);
}

export function SuccessMeetingPrepAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const id = useId();
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('SuccessMeetingPrepAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const conversationRef = useRef<StickToBottomContext>(null);
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: SUCCESS_MEETING_PREP_CONVERSATION_FILTER,
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createSuccessMeetingPrepConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createSuccessMeetingPrepConversation({
			assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createSuccessMeetingPrepConversation({
				assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createSuccessMeetingPrepConversation]);

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation contextRef={conversationRef}>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I&apos;ll help you prepare for your meeting with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}.
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmail}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContent}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator messages={AI_THINKING_MESSAGES} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}
